import { useEffect, useRef, useState } from 'react';

interface Props {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Si true, autorise l'effacement complet → propage null (sinon → 0). */
  allowNull?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Cellule d'édition numérique pour les lignes du tableau de facturation.
 *
 * Pourquoi un composant dédié : un simple <input controlled> sur un Number
 * empêche la saisie progressive ("0.", "-", "-.", "10.5", "0.01") parce
 * qu'on re-affiche systématiquement la valeur parsée et que le caractère
 * non parsable disparaît.
 *
 * On garde un état texte local indépendant. Le parent reçoit le `number`
 * (ou `null`) uniquement quand le texte est un nombre valide. Si le parent
 * pousse une nouvelle valeur (ex: conversion devise), on resynchronise.
 *
 * Accepte :
 *   - signe négatif ("-0.01", "-10")
 *   - virgule ET point décimal (normalisés en point)
 *   - saisies intermédiaires "-", ".", "-.", "0." (préservées dans le champ
 *     mais pas propagées au parent tant que ce n'est pas un nombre valide)
 */
export function NumberCell({ value, onChange, allowNull, placeholder, className }: Props) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  // Pour distinguer "le parent a poussé une nouvelle valeur" de "c'est nous
  // qui avons propagé un changement", on mémorise la dernière valeur émise.
  const lastEmitted = useRef<number | null>(value);

  // Resync quand la prop value change *en dehors* de notre propre onChange.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setText(value == null ? '' : String(value));
  }, [value]);

  const handleChange = (raw: string) => {
    setText(raw);
    const trimmed = raw.trim();
    // Effacement complet.
    if (trimmed === '') {
      const next = allowNull ? null : 0;
      lastEmitted.current = next;
      onChange(next);
      return;
    }
    // Saisies intermédiaires : on garde le texte affiché mais on ne propage pas.
    if (trimmed === '-' || trimmed === '.' || trimmed === '-.' || trimmed === ',') return;

    // Normalisation virgule → point.
    const normalized = trimmed.replace(',', '.');
    // Garde les saisies "0." ou "-0." (point final) → pas encore un nombre stable.
    if (/^-?\d*\.$/.test(normalized)) return;

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) return; // saisie invalide → on ignore en propagation
    lastEmitted.current = parsed;
    onChange(parsed);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onChange={(e) => handleChange(e.target.value)}
      className={
        className ??
        'w-full rounded px-2 py-1 text-sm text-right tabular-nums border border-slate-200 bg-white hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
      }
    />
  );
}
